import React, { useState } from 'react'
import useSWR from 'swr'
import { deleteImage, fetchCompetitionLevels, uploadImage } from '../services/api'
import { ImageSlot } from './ImageSlot'
import { Modal } from './Modal'
import { useSpeedbump } from './Speedbump'
import listTableStyles from '../styles/list-table.module.scss'
import pageStyles from '../styles/page.module.scss'
import styles from './CompetitionLevelLogosModal.module.scss'

interface CompetitionLevelLogo {
  image_id: string
  url: string | null
  type_code: string
}

interface CompetitionLevelItem {
  tc_competition_level_id: string
  ext_level: string | null
  tc_level: string | null
  tc_display_name: string
  logo_image: CompetitionLevelLogo | null
}

interface CompetitionLevelsResponse {
  competition_levels: CompetitionLevelItem[]
  timestamp: string
}

interface CompetitionLevelLogosModalProps {
  isOpen: boolean
  onClose: () => void
}

const getErrorMessage = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message : fallback

export const CompetitionLevelLogosModal: React.FC<CompetitionLevelLogosModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [actionMessage, setActionMessage] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const confirm = useSpeedbump()

  const { data, error, isLoading, mutate } = useSWR<CompetitionLevelsResponse>(
    isOpen ? 'competition-levels' : null,
    fetchCompetitionLevels,
    { revalidateOnFocus: false }
  )

  const competitionLevels = data?.competition_levels || []

  const handleUpload = async (level: CompetitionLevelItem, file: File) => {
    setActionError(null)
    setActionMessage(null)
    try {
      await uploadImage('competitionLevel', level.tc_competition_level_id, file, {
        alt_text: `${level.tc_display_name} logo`,
        image_type: 'competition_level_logo',
      })
      await mutate()
      setActionMessage(`${level.tc_display_name} logo updated.`)
    } catch (uploadError: unknown) {
      const msg = getErrorMessage(uploadError, 'Failed to upload logo.')
      setActionError(msg)
      throw new Error(msg)
    }
  }

  const handleDelete = async (level: CompetitionLevelItem) => {
    if (!level.logo_image) return
    const confirmed = await confirm({
      title: 'Delete logo?',
      message: `Delete the logo for ${level.tc_display_name}?`,
      confirmLabel: 'Delete',
      confirmVariant: 'danger',
    })
    if (!confirmed) return
    setActionError(null)
    setActionMessage(null)
    try {
      await deleteImage(
        'competitionLevel',
        level.tc_competition_level_id,
        level.logo_image.image_id,
      )
      await mutate()
      setActionMessage(`${level.tc_display_name} logo deleted.`)
    } catch (deleteError: unknown) {
      const msg = getErrorMessage(deleteError, 'Failed to delete logo.')
      setActionError(msg)
      throw new Error(msg)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Tournament Level Logos"
      size="large"
    >
      <div className={styles.intro}>
        Shared logos used across tournaments by level
      </div>

      {actionError && (
        <div className={pageStyles.error}>
          <p>{actionError}</p>
        </div>
      )}

      {actionMessage && (
        <div className={styles.successMessage}>{actionMessage}</div>
      )}

      {error ? (
        <div className={pageStyles.error}>
          <h2>Error loading competition levels</h2>
          <p>{(error as Error).message}</p>
        </div>
      ) : isLoading && !data ? (
        <div className={pageStyles.loading}>
          <h3>Loading level logos...</h3>
          <p>Please wait while we fetch competition levels.</p>
        </div>
      ) : (
        <div className={styles.tableWrap}>
          <div className={`${listTableStyles.tableHeader} ${styles.tableRow}`}>
            <div>Level</div>
            <div>Logo</div>
          </div>

          <div className={listTableStyles.tableBody}>
            {competitionLevels.map((level) => (
              <div
                key={level.tc_competition_level_id}
                className={`${listTableStyles.tableRow} ${styles.tableRow}`}
              >
                <div className={listTableStyles.cellPrimary}>
                  <p className={listTableStyles.primaryText}>{level.tc_display_name}</p>
                </div>
                <div className={styles.logoCell}>
                  <ImageSlot
                    image={level.logo_image?.url ? { url: level.logo_image.url } : null}
                    aspectRatio="1 / 1"
                    onUpload={(file) => handleUpload(level, file)}
                    onRemove={level.logo_image ? () => handleDelete(level) : undefined}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Modal>
  )
}
